import { useContext, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import config from '@/config';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import Loader from '@/shared-components/loader/Loader';

import SocialsHeader from '../components/SocialsHeader';

import RateArtworkForm from './rate-artwork-form';
import VirtualList from './virtual-list';

import './index.css';

export default function ArtworkRatings({ closeSocial }) {
  const { artworkData, socialsData, setSocialsData } = useContext(ArtworkDataContext);
  const { state } = useStateHandler();
  const location = useLocation();
  const viewerSession = state?.user_session;
  const isGallery = Boolean(viewerSession?.gallery_id)
    || [viewerSession?.user_type, viewerSession?.__t]
      .some((role) => String(role ?? '').toLowerCase() === 'gallery');

  const { loading, fetchData, setFetchData, loadMoreData, refetch } = useCache(
    `artwork_${artworkData.slug}_ratings`,
    `${config.apis.api.url}/rating/artwork/${artworkData._id}`,
    {
      type: "@cache/dynamic",
      injectToken: true,
      invalidateWhen: ["NEW_RATING"],
    }
  );
  const ratingsContainer = useRef(null);
  const revealRatingEntry = location.state?.revealRatingEntry ?? null;

  const displayRatingsData = useMemo(() => {
    if (!fetchData) {
      return fetchData;
    }

    if (!fetchData.my_rating || !revealRatingEntry?.rating_creator) {
      return fetchData;
    }

    const revealCreatorId = `${revealRatingEntry.rating_creator?._id ?? revealRatingEntry.rating_creator?.id ?? ''}`;
    if (!revealCreatorId) {
      return fetchData;
    }

    const currentData = Array.isArray(fetchData.data) ? fetchData.data : [];
    const matchingIndex = currentData.findIndex((rating) => {
      const ratingCreatorId = `${rating?.rating_creator?._id ?? rating?.rating_creator?.id ?? ''}`;
      return ratingCreatorId === revealCreatorId;
    });

    if (matchingIndex === 0) {
      return fetchData;
    }

    if (matchingIndex > 0) {
      const reordered = [...currentData];
      const [matchingRating] = reordered.splice(matchingIndex, 1);
      reordered.unshift(matchingRating);
      return {
        ...fetchData,
        data: reordered,
      };
    }

    return {
      ...fetchData,
      data: [revealRatingEntry, ...currentData],
    };
  }, [fetchData, revealRatingEntry]);
    
  function onRateArtwork(myRating) {
    ratingsContainer.current.classList.add("ratings-animation");
        
    if (socialsData) {
      setSocialsData({
        ...socialsData,
        published_rating: true,
        ratings_count: socialsData.ratings_count + 1
      });
    }

    setFetchData((currentData) => {
      if (!currentData) {
        return {
          my_rating: myRating,
          data: [],
          totalDocs: 0,
          emotions_average: 50,
          style_average: 50,
          time_travel_average: 50,
        };
      }

      return {
        ...currentData,
        my_rating: myRating,
      };
    });

    void refetch();
  }

  return (
    <div
      className="artwork-view-ratings__outter-container"
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      ref={ratingsContainer}
    >
      <SocialsHeader close={closeSocial} type="ratings" />
      
      <div className="artwork-view-ratings__content-container">
        {loading && !fetchData ? (
          <Loader className="artwork-view-ratings-loading__spinner" thin />
        ) : (isGallery || Boolean(fetchData?.my_rating)) ? (
          <VirtualList
            ratingsData={displayRatingsData ?? { data: [], totalDocs: 0 }}
            onLoadMore={loadMoreData}
            isGallery={isGallery}
          />
        ) : (
          <RateArtworkForm onRateArtwork={onRateArtwork} />
        )}
      </div>
    </div>
  );
}
